import {
  Button,
  Checkbox,
  Divider,
  Input,
  Row,
  Spin,
  Table,
  message,
} from 'antd'
import axios from 'axios'
import React from 'react'
import { IconTray } from '../../../../../components/CustomReturnsIcons'
import StoreWideProducts from '../../../../portal/storeWideExchanges/StoreWideProducts'

const { Search } = Input

class WarrantyProducts extends StoreWideProducts {
  fetchWarrantyProductsPage = async (endCursor) => {
    const response = await axios.post('/api/returns/getStoreWideProducts', {
      companyIdentifier: this.props.companyIdentifier,
      hasNextPage: true,
      endCursor,
    });

    if (response.status !== 200) {
      throw new Error('Error while getting products from Shopify');
    }

    // An empty page is a valid "no more products" signal (e.g. hasNextPage was
    // stale, or the store's remaining product count doesn't fill a full page)
    // and must not be treated as a fetch failure, otherwise selecting a larger
    // page size than what's already loaded silently reverts to the old value.
    return {
      products: response.data?.products || [],
      hasNextPage: response.data?.products?.length ? response.data?.hasNextPage : false,
      endCursor: response.data?.endCursor,
    };
  }

  loadWarrantyProductsThroughPage = async (currentPage, pageSize) => {
    let products = [...this.state.products];
    let hasNextPage = this.state.hasNextPage;
    let endCursor = this.state.endCursor;
    const requiredProductCount = currentPage * pageSize;

    while (products.length < requiredProductCount && hasNextPage && endCursor) {
      const response = await this.fetchWarrantyProductsPage(endCursor);
      products = [...products, ...response.products];
      hasNextPage = response.hasNextPage;
      endCursor = response.endCursor;
    }

    return {
      products,
      hasNextPage,
      endCursor,
    };
  }

  handleWarrantyPageChange = async (currentPage, pageSize) => {
    const currentPageSize = this.state.pageSize || 5;
    const nextPage = pageSize === currentPageSize ? currentPage : 1;

    if (this.state.searchValue) {
      return this.setState({
        currentPage: nextPage,
        pageSize,
      });
    }

    try {
      this.setState({ loading: true });
      const {
        products,
        hasNextPage,
        endCursor,
      } = await this.loadWarrantyProductsThroughPage(nextPage, pageSize);

      this.setState({
        products,
        modalProducts: products,
        hasNextPage,
        endCursor,
        currentPage: nextPage,
        pageSize,
        loading: false,
      });
    } catch (error) {
      message.error('Error occurred during data retrieval', 4);
      this.setState({ loading: false });
    }
  }

  getWarrantyPaginationTotal = () => {
    const loadedProductCount = this.state.modalProducts.length;
    if (this.state.searchValue || !this.state.hasNextPage) return loadedProductCount;

    return loadedProductCount + (this.state.pageSize || 5);
  }

  isWarrantyProductLimitReached = () => {
    return this.props.warrantySelectionCount >= this.props.maxWarrantyProducts;
  }

  isWarrantyVariantSelected = (variantId) => {
    return this.props?.warrantyProducts?.some(field => field.variantId === variantId);
  }

  isWarrantyVariantPending = (variantId) => {
    return this.props?.pendingWarrantyProducts?.some(field => field.variantId === variantId);
  }

  getSelectableWarrantyVariantIds = (products = this.state.modalProducts) => {
    return products.flatMap(product =>
      product.variants
        .filter(variant => !this.isWarrantyVariantSelected(variant.id))
        .map(variant => variant.id)
    );
  }

  getWarrantySelectAllState = () => {
    const selectableVariantIds = this.getSelectableWarrantyVariantIds();
    const selectedCount = selectableVariantIds.filter(variantId => this.isWarrantyVariantPending(variantId)).length;
    const limitReached = this.isWarrantyProductLimitReached();

    return {
      checked: selectableVariantIds.length > 0 && selectedCount === selectableVariantIds.length,
      disabled: selectableVariantIds.length === 0 || (limitReached && selectedCount === 0),
      indeterminate: selectedCount > 0 && selectedCount < selectableVariantIds.length,
    };
  }

  getWarrantyProductSelectionState = (product) => {
    const variants = product.variants || [];
    const selectableVariants = variants.filter(variant => !this.isWarrantyVariantSelected(variant.id));
    const selectedVariantCount = variants.filter(variant =>
      this.isWarrantyVariantSelected(variant.id) || this.isWarrantyVariantPending(variant.id)
    ).length;
    const pendingVariantCount = variants.filter(variant => this.isWarrantyVariantPending(variant.id)).length;

    return {
      checked: variants.length > 0 && selectedVariantCount === variants.length,
      disabled: selectableVariants.length === 0
        || (this.isWarrantyProductLimitReached() && pendingVariantCount === 0),
      indeterminate: selectedVariantCount > 0 && selectedVariantCount < variants.length,
    };
  }

  handleToggleVisibleWarrantyProducts = (checked) => {
    this.props.handleToggleWarrantyProducts(this.state.modalProducts, checked);
  }

  getWarrantySelectionColumn = () => {
    const selectAllState = this.getWarrantySelectAllState();

    return {
      title: (
        <Checkbox
          checked={selectAllState.checked}
          disabled={selectAllState.disabled}
          indeterminate={selectAllState.indeterminate}
          onChange={(e) => this.handleToggleVisibleWarrantyProducts(e.target.checked)}
        />
      ),
      key: 'warrantySelection',
      width: 48,
      render: (product) => {
        const selectionState = this.getWarrantyProductSelectionState(product);

        return (
          <Checkbox
            checked={selectionState.checked}
            disabled={selectionState.disabled}
            indeterminate={selectionState.indeterminate}
            onChange={(e) => this.props.handleToggleWarrantyProductVariants(this.state.modalProducts, product, e.target.checked)}
          />
        );
      },
    };
  }

  handleRowRander = (row) => {
    const nestedColumns = [
      {
        title: '',
        key: 'warrantyVariantSelection',
        width: 48,
        render: (_, record) => {
          const isSelected = this.isWarrantyVariantSelected(record.id);
          const isPending = this.isWarrantyVariantPending(record.id);

          return (
            <Checkbox
              checked={isSelected || isPending}
              disabled={isSelected || (this.isWarrantyProductLimitReached() && !isPending)}
              onChange={(e) => this.props.handleToggleWarrantyProduct(this.state.modalProducts, record, e.target.checked, row)}
            />
          );
        },
      },
      {
        title: 'Title',
        key: 'title',
        render: (record) => <p>{record?.title}</p>,
        width: 240,
      },
      {
        title: 'Image',
        key: 'image',
        render: (product) => (
          <img
            src={product?.image?.src}
            style={{
              maxWidth: '100%',
              maxHeight: '100px',
              width: 'auto',
              height: 'auto',
            }}
            alt="Image"
          />
        ),
        width: 200,
      },
      {
        title: 'Action',
        key: 'action',
        width: 190,
        render: (_, record) => {
          const products = this.state.modalProducts;
          const fields = this.props?.warrantyProducts;
          const isExist = fields?.length > 0 && fields.find(field => field.variantId === record.id);
          const isPending = this.isWarrantyVariantPending(record.id);

          return (
            <div className='priceContainer'>
              <Button
                type={isExist ? "secondary" : "primary"}
                onClick={() => { this.props.handleWarrantyProduct(products, record, row) }}
                disabled={isExist || (this.isWarrantyProductLimitReached() && !isPending)}
                style={{ width: 'fit-content' }}
              >
                {isExist ? 'Selected' : 'Select'}
              </Button>
            </div>
          )
        }
      },
    ]

    return (
      <Table
        rowKey={(record) => record.id}
        columns={nestedColumns}
        dataSource={row.variants}
        pagination={{ defaultPageSize: 5 }}
        locale={{
          emptyText: (
            <div className="flex-row flex-middle-xxs flex-center-xxs">
              <div className="flex-col-xxs-12">
                <IconTray size="md" />
              </div>
              <div>
                <p className="TextBody TextBody--xxxs">No Data</p>
              </div>
            </div>
          ),
        }}
        style={{ marginRight: 30, marginTop: 30, marginBottom: 30 }}
      />
    )
  }

  getColumns = () => [
    this.getWarrantySelectionColumn(),
    {
      title: 'Product',
      dataIndex: 'title',
      key: 'title',
      width: 240,
      render: (text, record) => (
        <span>
          {text}
          {record.variants.length === 1 && record.variants[0].title !== "Default Title" && ` (${record.variants[0].title})`}
        </span>
      ),
    },
    {
      title: 'Image',
      key: 'image',
      width: 120,
      render: (product) => (
        <img
          src={product?.image?.src}
          style={{
            maxWidth: '100%',
            maxHeight: '100px',
            width: 'auto',
            height: 'auto',
          }}
          alt="Image"
        />
      ),
    },
    {
      title: 'Price',
      key: 'Price',
      width: 210,
      render: (product) => {
        const products = this.state.modalProducts;
        const singleVariant = product.variants.length === 1

        if (singleVariant) {
          const fields = this.props?.warrantyProducts;
          const isExist = fields?.length > 0 && fields.find(field => field.variantId === product.variants[0].id);
          const isPending = this.isWarrantyVariantPending(product.variants[0].id);

          return (
            <div className='priceContainer' style={{ justifyContent: 'flex-end'}}>
              <Button
                type={isExist ? "secondary" : "primary"}
                onClick={() => {this.props.handleWarrantyProduct(products, product.variants[0], product)}}
                disabled={isExist || (this.isWarrantyProductLimitReached() && !isPending)}
                style={{ width: 'fit-content' }}
              >
                {isExist ? 'Selected' : 'Select'}
              </Button>
            </div>
          )
        }

        return (
          <div className='priceContainer' style={{ justifyContent: 'flex-end'}}>
            <Button type="primary" onClick={() => {this.handleRowExpansion(product.id)}} style={{ width: 'fit-content' }}>
              Expand
            </Button>
          </div>
        )
      },
    },
  ]

  render() {
    return (
      <React.Fragment>
        {this.state.loading ? (
          <Row type="flex" justify="space-around" align="middle">
            <Spin spinning={this.state.loading} tip="Loading Products"></Spin>
          </Row>
        ) : (
          <React.Fragment>
            <Row type="flex" justify="center" align="middle">
              <Search
                className='search-field'
                placeholder="Search for a Product"
                enterButton="Search"
                value={this.state.searchValue}
                onChange={this.handleSearchChange}
                style={{
                  width: '100%',
                  margin: '0px',
                }}
              />
            </Row>
            <Divider />
            <Table
              scroll={{ x: 768 }}
              rowKey={(record) => record.id}
              columns={this.getColumns()}
              expandedRowRender={this.handleRowRander}
              dataSource={this.state.modalProducts}
              pagination={{
                current: this.state.currentPage,
                pageSize: this.state.pageSize || 5,
                pageSizeOptions: ['5', '10', '20', '50', '100'],
                showSizeChanger: true,
                onChange: this.handleWarrantyPageChange,
                total: this.getWarrantyPaginationTotal(),
              }}
              expandedRowKeys={[this.state.expandedRowId]}
              onExpand={(_, record) => this.handleRowExpansion(record.id)}
              rowClassName={(record) => {
                if (record.variants.length === 1) return 'NotExpandible'
              }}
              locale={{
                emptyText: (
                  <div className="flex-row flex-middle-xxs flex-center-xxs">
                    <div className="flex-col-xxs-12">
                      <IconTray size="md" />
                    </div>
                    <div>
                      <p className="TextBody TextBody--xxxs">No Data</p>
                    </div>
                  </div>
                ),
              }}
            />
          </React.Fragment>
        )}
      </React.Fragment>
    )
  }
}

export default WarrantyProducts
